import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FaArrowAltCircleRight,
  FaArrowAltCircleLeft,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaFileDownload,
  FaPlus,
  FaSearch,
  FaEye,
  FaEyeSlash,
  FaSortUp,
  FaSortDown,
  FaSort,
} from 'react-icons/fa'
import { CSVLink } from 'react-csv'
import Modal from '@components/common/modal/Modal'
import { useGOMenuSearch } from '@components/groupOrders/UniversalMenuExplorer/useGOMenuSearch'
import Checkbox from '@components/common/form/Checkbox'
import Dropdown from '@components/common/form/Dropdown'
import NumberInputWithNull from '@components/common/form/NumberInputWithNull'
import VendorBlacklist from '@components/groupOrders/VendorBlacklist'
import MenuItemBlacklist from '@components/groupOrders/MenuItemBlacklist'
import { Label } from '@res/styledComponents/index'
import { AutocompleteInput, MultiSelectDropDown } from '@components/common/form'
import {
  ClientSettings,
  ItemBlacklistEntry,
  VendorBlacklistEntry,
  VendorGroup,
  PreviewMenu,
  PreviewMenuItem,
  Option,
  SearchUniversalExplorerMenusReq,
} from '@types'
import { ClosedBlacklistedBtn } from '@components/groupOrders/VendorBlacklist'
import Loader from '@components/common/Loader'
import { ExportButton } from '@components/groupOrders/SchedulePlanner/MenusFinder/GroupOrderMenusFinder'
import { vendorGroupsToCSV } from '@components/groupOrders/UniversalMenuExplorer/utils'
import {
  GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE,
  GROUP_ORDER_SUBSIDY_TYPE_PERCENT,
  GroupOrderSubsidyTypes,
} from '../constants'
import { capitalizeFirstLetter, parseMilitaryTimeToAMPM } from '@utils'
import { formatCutoff } from '@utils/groupOrderUtils'
import { MenuConcept } from '../../common/modal/PickMenuModal/types'

interface ViewSettings {
  expandedVendors: Set<string>
  expandedMenus: Set<string>
}

export type Mode = 'preview' | 'apply'
type SortKey = 'name' | 'margin' | 'price' | 'chefPrice'
type SortDir = 'asc' | 'desc'

interface UniversalMenuExplorerProps {
  hqId: number
  loadAllergens: () => Promise<Option[]>
  searchGOMenus: (params: SearchUniversalExplorerMenusReq) => Promise<any>
  searchClientSettings: () => void
  displayFailureMessage: (mssg: string) => void
  stagedMenus?: MenuConcept[]
  mode?: Mode
  initialClientSettings?: ClientSettings
  initialDayOfWeek?: number
  selectMenu?: (m: PreviewMenu, excludedItemIds: Set<string>) => void
}

const UniversalMenuExplorer: React.FC<UniversalMenuExplorerProps> = ({
  hqId,
  mode = 'preview',

  initialClientSettings,
  initialDayOfWeek,
  stagedMenus = [],

  loadAllergens,
  searchClientSettings,
  searchGOMenus,
  displayFailureMessage,
  selectMenu,
}) => {
  const [allergens, setAllergens] = useState<Option[]>([])
  const [selectedClientSettings, setSelectedClientSettings] =
    useState<ClientSettings | null>()
  const [showVendorBlacklist, setShowVendorBlacklist] = useState(false)
  const [vendorBlacklist, setVendorBlacklist] = useState<
    VendorBlacklistEntry[]
  >([])
  const [menuItemBlacklist, setMenuItemBlacklist] = useState<
    ItemBlacklistEntry[]
  >([])
  const [showItemBlacklist, setShowItemBlacklist] = useState(false)
  const [manuallyExcludedItems, setManuallyExlcudedItems] = useState<
    Record<string, Set<string>>
  >({})
  const [search, setSearch] = useState<string>('')
  const [viewSettings, setViewSettings] = useState<ViewSettings>({
    expandedMenus: new Set(),
    expandedVendors: new Set(),
  })
  const [initialMenusLoaded, setInitialMenusLoaded] = useState(false)
  const [includeNonUniversal, setIncludeNonUniversal] = useState(false)
  const [showFilters, toggleShowFilters] = useState(true)
  const [showReasonsColumn, setShowReasonsColumn] = useState(false)
  const [serverSearch, setServerSearch] = useState('')
  const [page, setPage] = useState(0)
  const limit = 50
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const blacklistedItemIds = useMemo(
    () =>
      new Set(menuItemBlacklist.filter((e) => e.active).map((e) => e.itemId)),
    [menuItemBlacklist],
  )
  const blacklistedVendorIds = useMemo(
    () =>
      new Set(vendorBlacklist.filter((e) => e.active).map((e) => e.vendorId)),
    [vendorBlacklist],
  )
  const filtersReadOnly = mode === 'apply'

  const {
    loadingMenus,
    menus,
    filters,

    resetFilters,
    setEnableSides,
    setEnableModifications,
    setExcludedDietaryTags,
    setFlatPriceMarginMin,
    setSubisdyType,
    setSubsidyAmt,
    setDayOfWeek,
    setMaxPriceLimit,
    setMaxModPriceLimit,
    setMaxItemTotalPriceLimit,
  } = useGOMenuSearch({
    hqId,
    blacklistedItemIds,
    blacklistedVendorIds,
    search,
    serverSearch,
    mode,
    page,
    limit,
    includeNonUniversal,
    displayFailureMessage,
    searchGOMenus,
  })

  const setFiltersFromUniversalMenuCfg = useCallback(
    (clientSettings: ClientSettings, dayOfWeek: number | null) => {
      const { universalMenuConfig: cfg, subsidySettings } = clientSettings
      if (cfg) {
        const {
          enableSides,
          enableModifications,
          excludedDietaryTags = [],
          flatPriceMarginMinimum,
          maxItemPriceLimit,
          maxModificationPriceLimit,
          maxTotalItemPriceLimit,
          vendorBlacklist,
          menuItemBlacklist,
        } = cfg

        setEnableSides(enableSides)
        setEnableModifications(enableModifications)
        setExcludedDietaryTags(excludedDietaryTags)
        setMaxPriceLimit(maxItemPriceLimit)
        setMaxModPriceLimit(maxModificationPriceLimit)
        setMaxItemTotalPriceLimit(maxTotalItemPriceLimit)
        setVendorBlacklist(vendorBlacklist)
        setMenuItemBlacklist(menuItemBlacklist)

        if (flatPriceMarginMinimum) {
          setSubisdyType(GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE)
          setFlatPriceMarginMin(flatPriceMarginMinimum)
        }

        const activeSubs = subsidySettings.subsidies.filter((s) => s.isActive)
        const selected =
          dayOfWeek != null
            ? activeSubs.find((s) => s.days?.includes(dayOfWeek))
            : activeSubs[0]

        if (selected) {
          if (!flatPriceMarginMinimum) {
            setSubisdyType(selected.subsidyType)
          }
          const { perOrderSubsidy } = selected
          if (perOrderSubsidy) {
            setSubsidyAmt(Number(perOrderSubsidy))
          }
        }
      }
    },
    [
      setEnableSides,
      setEnableModifications,
      setExcludedDietaryTags,
      setMaxPriceLimit,
      setMaxModPriceLimit,
      setMaxItemTotalPriceLimit,
      setVendorBlacklist,
      setMenuItemBlacklist,
      setSubisdyType,
      setSubsidyAmt,
      setFlatPriceMarginMin,
    ],
  )

  const isSelected = useCallback(
    (menuId: string) => Boolean(stagedMenus.find((s) => s.id === menuId)),
    [stagedMenus],
  )

  useEffect(() => {
    if (initialClientSettings && initialClientSettings.universalMenuConfig) {
      // const { subsidySettings, universalMenuConfig } = initialClientSettings
      const day = initialDayOfWeek == undefined ? null : initialDayOfWeek
      setFiltersFromUniversalMenuCfg(initialClientSettings, day)
    }
  }, [initialClientSettings, initialDayOfWeek, setFiltersFromUniversalMenuCfg])

  useEffect(() => {
    const fetchAllergens = async () => {
      const optionAllergens = await loadAllergens()
      setAllergens(optionAllergens)
    }

    fetchAllergens()
  }, [loadAllergens])

  useEffect(() => {
    if (!initialMenusLoaded && menus.length > 0) {
      const expandedVendorIds = menus.map((m) => m.vendorId)
      const expandedMenuIds = menus.flatMap((m) => m.menus.map((m) => m.id))
      setViewSettings({
        expandedMenus: new Set(expandedMenuIds),
        expandedVendors: new Set(expandedVendorIds),
      })
      setInitialMenusLoaded(true)
    }
  }, [menus, initialMenusLoaded])

  const csvData = vendorGroupsToCSV(menus)
  const csvFilename = `menus-${new Date().toISOString().split('T')[0]}.csv`

  const requestSort = (key: SortKey) => {
    setSortKey((prevKey) => {
      if (prevKey !== key) {
        setSortDir('asc')

        return key
      }
      setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'))

      return prevKey
    })
  }

  const onSelectMenu = (menu: PreviewMenu) => {
    if (selectMenu) {
      const excludedItemSet = manuallyExcludedItems[menu.id] || new Set()
      selectMenu(menu, excludedItemSet)
    }
  }

  const toggleManuallyExlcudedItems = (itemId: string, menuId: string) => {
    const next = { ...manuallyExcludedItems }
    const menuSet = next[menuId]
    if (!menuSet) {
      next[menuId] = new Set()
      next[menuId].add(itemId)
    } else if (menuSet.has(itemId)) {
      menuSet.delete(itemId)
    } else if (!menuSet.has(itemId)) {
      menuSet.add(itemId)
    }

    setManuallyExlcudedItems(next)
  }

  const toggleVendor = (vendorId: string) => {
    const newExpanded = new Set(viewSettings.expandedVendors)
    if (newExpanded.has(vendorId)) {
      newExpanded.delete(vendorId)
    } else {
      newExpanded.add(vendorId)
    }
    setViewSettings((prev) => ({ ...prev, expandedVendors: newExpanded }))
  }

  const toggleMenu = (menuId: string) => {
    const newExpanded = new Set(viewSettings.expandedMenus)
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId)
    } else {
      newExpanded.add(menuId)
    }
    setViewSettings((prev) => ({ ...prev, expandedMenus: newExpanded }))
  }

  const expandAllVendors = () => {
    setViewSettings((prev) => ({
      ...prev,
      expandedVendors: new Set(menus.map((m) => m.vendorId)),
    }))
  }

  const collapseAllVendors = () => {
    setViewSettings((prev) => ({ ...prev, expandedVendors: new Set() }))
  }

  const expandAllMenus = () => {
    const expandedMenuIds = menus.flatMap((m) => m.menus.map((m) => m.id))
    setViewSettings((prev) => ({
      ...prev,
      expandedMenus: new Set(expandedMenuIds),
    }))
  }

  const collapseAllMenus = () => {
    setViewSettings((prev) => ({ ...prev, expandedMenus: new Set() }))
  }

  const handleSelectClientSettings = (cs: ClientSettings) => {
    setSelectedClientSettings(cs)
    if (cs.universalMenuConfig) {
      setFiltersFromUniversalMenuCfg(cs, filters.dayOfWeek)
    }
  }

  const handleSearchSubmit = () => {
    if (mode !== 'apply') {
      return
    }
    setPage(0)
    setServerSearch(search)
  }

  const handleClearClientSettings = () => {
    setSelectedClientSettings(null)
    setVendorBlacklist([])
    setMenuItemBlacklist([])
    resetFilters()
  }

  const onOpenVendorsBlacklist = () => {
    setShowVendorBlacklist(true)
  }

  const onOpenMenuItemsBlacklist = () => {
    setShowItemBlacklist(true)
  }

  const vendorBlacklistSet = useMemo(
    () =>
      new Set(vendorBlacklist.filter((e) => e.active).map((e) => e.vendorId)),
    [vendorBlacklist],
  )
  const numberOfMenus = menus.flatMap((m) => m.menus).length

  return (
    <div className="flex flex-col">
      {showItemBlacklist && (
        <Modal
          title="Menu Items Blacklist"
          onHide={() => setShowItemBlacklist(false)}
          color="#001940"
          width="900px"
        >
          <MenuItemBlacklist
            list={menuItemBlacklist}
            editable={false}
            close={() => setShowItemBlacklist(false)}
          />
        </Modal>
      )}
      {showVendorBlacklist && (
        <Modal
          title="Vendor Blacklist"
          onHide={() => setShowVendorBlacklist(false)}
          color="#001940"
          width="900px"
        >
          <VendorBlacklist
            list={vendorBlacklist}
            editable={false}
            close={() => setShowVendorBlacklist(false)}
          />
        </Modal>
      )}
      {mode == 'preview' && (
        <label className="font-bold text-teal-600 text-lg pb-3 underline">
          Discovery view: explore how changing client settings and Universal
          Menu configs affects menu/item filtering.
        </label>
      )}
      <div className="flex gap-6">
        {/* <SettingsSidebar /> */}
        {showFilters && (
          <div className="w-80 bg-white rounded-lg shadow-md border border-gray-300 border-solid p-6 h-fit max-w-xs max-h-950">
            {mode == 'preview' ? (
              <div className="space-y-6 pb-6">
                <div>
                  <h3 className="font-semibold text-lg mb-2">
                    Client Settings
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Select a client to autopopulate settings, then edit these
                    settings to see how they affect menu options.
                  </p>
                </div>
                <div className="flex flex-col space-y-2">
                  <Label>Select Client Settings </Label>
                  <AutocompleteInput
                    value={selectedClientSettings?.name}
                    loaderFunction={searchClientSettings}
                    onSelect={(cs: ClientSettings) =>
                      handleSelectClientSettings(cs)
                    }
                    onClear={() => handleClearClientSettings()}
                  />
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-2">
                  <p className="text-md font-semibold uppercase text-gray-600 mb-1">
                    Viewing Options
                  </p>

                  <Checkbox
                    label="Include Non-Universal Menus"
                    checked={includeNonUniversal}
                    onChange={() => {
                      setIncludeNonUniversal(!includeNonUniversal)
                      setPage(0)
                    }}
                  />
                </div>

                <div className="border border-solid border-t border-gray-200 my-2" />
                {/* Locked Noticed */}
                <div className="flex flex-col items-center mb-4">
                  <p className="text-md font-bold text-gray-800 mb-1">
                    Filters cannot be changed while selecting menus. Please
                    update your client settings universal menu configuration.
                  </p>
                  <p className="text-sm font-semibold uppercase text-gray-600">
                    Current Client Universal GO Menu Configurations
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-4 pb-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  label="Enable Sides"
                  id="enable-sides"
                  checked={filters.enableSides}
                  onChange={() => setEnableSides(!filters.enableSides)}
                  disabled={filtersReadOnly}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  label="Enable Modifications"
                  id="enable-modifications"
                  checked={filters.enableMods}
                  onChange={() => setEnableModifications(!filters.enableMods)}
                  disabled={filtersReadOnly}
                />
              </div>
            </div>

            {/* Subsidy */}
            <div className="space-y-4">
              <Dropdown
                label="Subsidy Settings"
                value={filters.subsidyType}
                disabled={filtersReadOnly}
                onChange={(e) => {
                  let { value } = e.target
                  if (!value) {
                    value = null
                  }
                  setSubisdyType(value)
                }}
              >
                <option value={''}></option>
                {GroupOrderSubsidyTypes.map((sT) => (
                  <option key={sT} value={sT}>
                    {sT}
                  </option>
                ))}
              </Dropdown>
              {filters.subsidyType && (
                <div>
                  <Label>Subsidy Amount</Label>
                  <NumberInputWithNull
                    value={filters.subsidyAmt}
                    onChange={(val: number | null) => setSubsidyAmt(val)}
                    className="h-10"
                    disabled={filtersReadOnly}
                    min={0}
                    step={1}
                    max={
                      filters.subsidyType == GROUP_ORDER_SUBSIDY_TYPE_PERCENT
                        ? 100
                        : undefined
                    }
                    asPercent={
                      filters.subsidyType == GROUP_ORDER_SUBSIDY_TYPE_PERCENT
                    }
                  />
                </div>
              )}
              {mode == 'preview' && (
                <div>
                  <Label>Day of Week</Label>
                  <Dropdown
                    value={filters.dayOfWeek}
                    onChange={(e) => setDayOfWeek(e.target.value)}
                  >
                    <option></option>
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                    <option value={7}>Sunday</option>
                  </Dropdown>
                </div>
              )}
              {filters.subsidyType == GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE && (
                <div>
                  <Label>Flat Price Margin Minimum</Label>
                  <NumberInputWithNull
                    value={filters.flatPriceMarginMin}
                    className="h-10"
                    min={0}
                    step={1}
                    onChange={(val: number | null) =>
                      setFlatPriceMarginMin(val)
                    }
                    asPercent={true}
                    disabled={filtersReadOnly}
                  />
                </div>
              )}
              <div>
                <Label>Max Price Limit ($)</Label>
                <NumberInputWithNull
                  value={filters.maxPriceLimit}
                  className="h-10"
                  onChange={(val: number | null) => setMaxPriceLimit(val)}
                  min={0}
                  disabled={filtersReadOnly}
                />
              </div>
              <div>
                <Label>Max Modification Price Limit ($)</Label>
                <NumberInputWithNull
                  value={filters.maxModPriceLimit}
                  className="h-10"
                  onChange={(val: number | null) => setMaxModPriceLimit(val)}
                  min={0}
                  disabled={filtersReadOnly}
                />
              </div>
              <div>
                <Label> Max Total Price Limit (Base Item + Mods) ($)</Label>
                <NumberInputWithNull
                  value={filters.maxTotalItemPriceLimit}
                  className="h-10"
                  onChange={(val: number | null) =>
                    setMaxItemTotalPriceLimit(val)
                  }
                  min={filters.maxPriceLimit || 0}
                  disabled={filtersReadOnly}
                />
              </div>
              <div>
                <MultiSelectDropDown
                  label="Exclude Dietary Preferences"
                  width="100%"
                  includeSelectAll={false}
                  renderCheckedOptions={filtersReadOnly}
                  options={allergens}
                  checkedOptions={filters.excludedDietaryTags}
                  searchPlaceholder=""
                  onChange={(newOptions) => setExcludedDietaryTags(newOptions)}
                  disabled={filtersReadOnly}
                />
              </div>
              <div className="flex flex-col space-y-4 w-full">
                <ClosedBlacklistedBtn
                  label="Blacklisted Vendors"
                  listCount={vendorBlacklist.filter((e) => e.active).length}
                  toggleOpen={onOpenVendorsBlacklist}
                />
                <ClosedBlacklistedBtn
                  label="Blacklisted Menu Items"
                  listCount={menuItemBlacklist.filter((e) => e.active).length}
                  toggleOpen={onOpenMenuItemsBlacklist}
                />
              </div>
            </div>
          </div>
        )}
        <div className="flex-1">
          {/* Settings Header */}
          <div className="bg-white rounded-lg shadow-md border p-4 mb-4">
            <div className="flex flex-row items-center pb-3">
              <div className="flex flex-col w-40 ">
                <label className="flex items-center cursor-pointer">
                  <div
                    className="relative"
                    style={{ width: '60px', height: '32px' }}
                  >
                    <input
                      type="checkbox"
                      checked={showFilters}
                      onChange={() => toggleShowFilters(!showFilters)}
                      className="sr-only"
                    />
                    <div
                      className="rounded-full absolute inset-0"
                      style={{
                        backgroundColor: showFilters ? '#ff624d' : '#cbd5e0',
                        transition: 'background-color 0.3s ease-in-out',
                      }}
                    />
                    <div
                      className="rounded-full bg-white absolute"
                      style={{
                        height: '24px',
                        width: '24px',
                        top: '4px',
                        left: showFilters ? '32px' : '4px',
                        transition: 'left 0.3s ease-in-out',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        opacity: '1',
                      }}
                    />
                  </div>
                  <span className="ml-3 bold font-medium text-gray-700">
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                  </span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-start mb-4">
              <div className="flex items-center justify-between w-full">
                {mode === 'apply' && (
                  <div className="flex items-center space-x-2">
                    {page > 0 && (
                      <button
                        className="p-2 rounded-md border border-gray-600 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        disabled={page <= 0}
                        onClick={() => {
                          const newPage = page - 1
                          setPage(newPage)
                        }}
                        title="Previous page"
                      >
                        <FaArrowAltCircleLeft size={18} />
                      </button>
                    )}
                    <span className="px-3 py-1 bg-gray-200 rounded-md text-sm font-medium text-gray-700 min-w-[80px] text-center">
                      Page {page + 1}
                    </span>
                    {numberOfMenus == limit && (
                      <button
                        className="p-2 rounded-md border border-gray-600 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        onClick={() => {
                          const newPage = page + 1
                          setPage(newPage)
                        }}
                        title="Next page"
                      >
                        <FaArrowAltCircleRight size={18} />
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-4 w-1/3 max-w-xs">
                  {menus.length > 0 && mode !== 'apply' && (
                    <CSVLink
                      data={csvData}
                      filename={csvFilename}
                      style={{
                        textDecoration: 'none',
                        width: '100%',
                        justifyItems: 'center',
                      }}
                      target="_blank"
                    >
                      <ExportButton>
                        <FaFileDownload />
                        Export to CSV
                      </ExportButton>
                    </CSVLink>
                  )}
                </div>
                <div className="flex flex-row items-center">
                  <div className="flex flex-row items-center justify-start gap-2 border-solid border-gray-400 border rounded-md p-2 mr-2">
                    <FaSearch className=" text-gray-400" />
                    <input
                      placeholder="Search menus, vendors, items..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (mode === 'apply' && e.key == 'Enter') {
                          handleSearchSubmit()
                        }
                      }}
                      className="w-64"
                    />
                  </div>
                  {mode === 'apply' && (
                    <button
                      onClick={handleSearchSubmit}
                      disabled={loadingMenus}
                      className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-gray-400 transition-colors whitespace-nowrap"
                    >
                      {loadingMenus ? 'Searching...' : 'Search'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <button
                    className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                    onClick={expandAllVendors}
                  >
                    <FaChevronDown className="h-4 w-4 mx-1" />
                    Expand All Vendors
                  </button>
                  <button
                    className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                    onClick={collapseAllVendors}
                  >
                    <FaChevronUp className="h-4 w-4 mx-1" />
                    Collapse All Vendors
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                    onClick={expandAllMenus}
                  >
                    <FaChevronDown className="h-4 w-4 mx-1" />
                    Expand All Menus
                  </button>
                  <button
                    className="flex flex-row items-center py-2 px-1 border-solid border-gray-600 border rounded-lg hover:bg-gray-200 font-semibold"
                    onClick={collapseAllMenus}
                  >
                    <FaChevronUp className="h-4 w-4 mx-1" />
                    Collapse All Menus
                  </button>
                </div>

                <div className="text-sm text-gray-600">
                  {numberOfMenus} Menus Found
                </div>
              </div>
              <div className="flex flex-row items-center">
                <button
                  onClick={() => setShowReasonsColumn(!showReasonsColumn)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md bg-teal-500 text-white hover:bg-teal-600 transition-colors text-sm"
                >
                  {showReasonsColumn ? (
                    <>
                      <FaEyeSlash className="h-4 w-4" />
                      Hide Reasons
                    </>
                  ) : (
                    <>
                      <FaEye className="h-4 w-4" />
                      Show Reasons
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Table  */}
          <div className="bg-white rounded-lg shadow-md border">
            <div className="overflow-x-auto rounded-lg">
              <table className="w-full">
                <thead className="bg-teal-500 text-white">
                  <tr>
                    {mode === 'apply' && (
                      <th
                        className="px-2 py-3 text-left font-medium"
                        style={{ maxWidth: '65px' }}
                      >
                        Manually Exlcude
                      </th>
                    )}
                    <th
                      className="px-4 py-3 text-left font-medium cursor-pointer flex flex-row items-center"
                      onClick={() => requestSort('name')}
                    >
                      <span className="inline-flex items-center gap-1">
                        <span className="font-medium text-white">
                          Item Name
                        </span>
                        {sortKey === 'name' ? (
                          sortDir === 'asc' ? (
                            <FaSortUp size={16} />
                          ) : (
                            <FaSortDown size={16} />
                          )
                        ) : (
                          <FaSort size={16} />
                        )}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left font-medium">
                      Meal Type
                    </th>
                    <th className="px-4 py-3 text-left font-medium">
                      Has Mods
                    </th>
                    <th className="px-4 py-3 text-left font-medium">
                      Customizable Atl Name
                    </th>
                    <th
                      className="px-4 py-3 text-left font-medium"
                      style={{ maxWidth: '150px' }}
                    >
                      Dietary Tags
                    </th>
                    <th
                      className="px-4 py-3 text-left cursor-pointer"
                      onClick={() => requestSort('chefPrice')}
                    >
                      <span className="inline-flex items-center gap-1">
                        <span className="font-medium text-white">
                          Vendor Price
                        </span>
                        {sortKey === 'chefPrice' ? (
                          sortDir === 'asc' ? (
                            <FaSortUp size={16} />
                          ) : (
                            <FaSortDown size={16} />
                          )
                        ) : (
                          <FaSort size={16} />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-4 py-3 text-left cursor-pointer"
                      onClick={() => requestSort('price')}
                    >
                      <span className="inline-flex items-center gap-1">
                        <span className="font-medium text-white">
                          Client Price
                        </span>
                        {sortKey === 'price' ? (
                          sortDir === 'asc' ? (
                            <FaSortUp size={16} />
                          ) : (
                            <FaSortDown size={16} />
                          )
                        ) : (
                          <FaSort size={16} />
                        )}
                      </span>
                    </th>
                    <th
                      className="px-4 py-3 text-left cursor-pointer"
                      onClick={() => requestSort('margin')}
                    >
                      <span className="inline-flex items-center gap-1">
                        <span className="font-medium text-white">
                          Food Margin
                        </span>
                        {sortKey === 'margin' ? (
                          sortDir === 'asc' ? (
                            <FaSortUp size={16} />
                          ) : (
                            <FaSortDown size={16} />
                          )
                        ) : (
                          <FaSort size={16} />
                        )}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left font-medium">
                      Subsidy Covered
                    </th>
                    <th className="px-4 py-3 text-left font-medium">
                      Employee Pays
                    </th>
                    {showReasonsColumn && (
                      <th className="px-4 py-3 text-left font-medium">
                        Reasons
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loadingMenus ? (
                    <Loader />
                  ) : (
                    menus.map((vendorGroup) => (
                      <VendorRow
                        key={vendorGroup.vendorId}
                        vendorGroup={vendorGroup}
                        isExpanded={viewSettings.expandedVendors.has(
                          vendorGroup.vendorId,
                        )}
                        showReasons={showReasonsColumn}
                        sortKey={sortKey}
                        sortDir={sortDir}
                        vendorBlacklisted={vendorBlacklistSet.has(
                          vendorGroup.vendorId,
                        )}
                        onToggle={() => toggleVendor(vendorGroup.vendorId)}
                        onToggleManuallyExclude={(
                          itemId: string,
                          menuId: string,
                        ) => toggleManuallyExlcudedItems(itemId, menuId)}
                        manuallyExcludedItems={manuallyExcludedItems}
                        onMenuToggle={toggleMenu}
                        isSelected={isSelected}
                        onSelectMenu={onSelectMenu}
                        expandedMenus={viewSettings.expandedMenus}
                        mode={mode}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UniversalMenuExplorer

interface VendorRowProps {
  isExpanded: boolean
  expandedMenus: Set<string>
  manuallyExcludedItems: Record<string, Set<string>>
  mode: Mode
  showReasons: boolean
  sortKey: SortKey
  sortDir: SortDir
  vendorGroup: VendorGroup
  vendorBlacklisted: boolean

  onToggle: () => void
  onToggleManuallyExclude: (id: string, menuId: string) => void
  onMenuToggle: (menuId: string) => void

  isSelected: (id: string) => boolean
  onSelectMenu: (menu: PreviewMenu) => void
}

const VendorRow: React.FC<VendorRowProps> = ({
  manuallyExcludedItems,
  mode,
  showReasons,
  sortDir,
  sortKey,
  vendorGroup,
  vendorBlacklisted,
  isExpanded,
  onToggle,
  onToggleManuallyExclude,
  onMenuToggle,
  isSelected,
  onSelectMenu,
  expandedMenus,
}) => {
  const dim = vendorBlacklisted ? 'opacity-50' : ''

  return (
    <>
      <tr
        className={`border-b border-gray-400 border-solid hover:bg-gray-200 ${
          vendorBlacklisted ? 'bg-gray-400' : ''
        }`}
      >
        <td
          className="px-4 py-3"
          colSpan={
            mode === 'apply' ? (showReasons ? 12 : 11) : showReasons ? 11 : 10
          }
        >
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 ${dim}`}>
              <button onClick={onToggle}>
                {isExpanded ? (
                  <FaChevronUp className="h-4 w-4" />
                ) : (
                  <FaChevronDown className="h-4 w-4" />
                )}
              </button>
              <span className="font-medium">{vendorGroup.vendorName}</span>
              <span className="text-gray-600 text-sm ml-4">
                Earliest Pickup:{' '}
                {vendorGroup.earliestPickup
                  ? parseMilitaryTimeToAMPM(vendorGroup.earliestPickup)
                  : 'N/A'}{' '}
                | Max Items/Day: {vendorGroup.maxItemsPerDay || 'N/A'} | Min GO
                Payout: {vendorGroup.minGOPayout || 'N/A'}
              </span>
            </div>
            {vendorBlacklisted && (
              <div className="text-xs font-semibold text-red-700 bg-red-200 p-2 rounded opacity-100">
                <ul className="list-inside space-y-1">
                  <li>Vendor Blacklisted</li>
                </ul>
              </div>
            )}
          </div>
        </td>
      </tr>

      {isExpanded &&
        !vendorBlacklisted &&
        vendorGroup.menus.map((menu) => (
          <MenuRow
            key={menu.id}
            manuallyExcludedItems={manuallyExcludedItems[menu.id] || new Set()}
            menu={menu}
            mode={mode}
            showReasons={showReasons}
            sortKey={sortKey}
            sortDir={sortDir}
            vendorBlacklisted={vendorBlacklisted}
            isExpanded={expandedMenus.has(menu.id)}
            onToggle={() => onMenuToggle(menu.id)}
            onToggleManuallyExclude={onToggleManuallyExclude}
            isSelected={isSelected}
            onSelectMenu={onSelectMenu}
          />
        ))}
    </>
  )
}

interface MenuRowProps {
  isExpanded: boolean
  manuallyExcludedItems: Set<string>
  menu: PreviewMenu
  mode: Mode
  showReasons: boolean
  sortKey: SortKey
  sortDir: SortDir
  vendorBlacklisted: boolean
  onToggle: () => void
  onToggleManuallyExclude: (id: string, menuId: string) => void
  isSelected: (id: string) => boolean
  onSelectMenu: (menu: PreviewMenu) => void
}

const MenuRow: React.FC<MenuRowProps> = ({
  menu,
  mode,
  isExpanded,
  showReasons,
  sortDir,
  sortKey,
  vendorBlacklisted,
  manuallyExcludedItems,
  onToggle,
  onToggleManuallyExclude,
  isSelected,
  onSelectMenu,
}) => {
  const menuAvailable = menu.availability.available && !vendorBlacklisted
  const checked = isSelected(menu.id)
  const [hoveredModsItemId, setHoveredModsItemId] = useState<string | null>(
    null,
  )

  const sortedItems = useMemo(() => {
    const arr = [...menu.allMenuItems]
    const dir = sortDir === 'asc' ? 1 : -1

    const compareItems = (a: PreviewMenuItem, b: PreviewMenuItem) => {
      switch (sortKey) {
        case 'name': {
          const an = (a.name || '').toLocaleLowerCase()
          const bn = (b.name || '').toLocaleLowerCase()
          if (an < bn) {
            return -1 * dir
          }
          if (an > bn) {
            return 1 * dir
          }

          return 0
        }
        case 'price': {
          const av = a.price
          const bv = b.price
          if (av < bv) {
            return -1 * dir
          }
          if (av > bv) {
            return 1 * dir
          }

          return 0
        }
        case 'chefPrice': {
          const av = a.chefPrice
          const bv = b.chefPrice
          if (av < bv) {
            return -1 * dir
          }
          if (av > bv) {
            return 1 * dir
          }

          return 0
        }
        case 'margin': {
          const av = a.margin
          const bv = b.margin
          if (av < bv) {
            return -1 * dir
          }
          if (av > bv) {
            return 1 * dir
          }

          return 0
        }
        default:
          return 0
      }
    }

    return arr
      .map((v, i) => ({ v, i }))
      .sort((a, b) => {
        const c = compareItems(a.v, b.v)

        return c !== 0 ? c : a.i - b.i
      })
      .map((x) => x.v)
  }, [menu.allMenuItems, sortKey, sortDir])

  const availableItems = sortedItems.filter(
    (item) => item.availability.available,
  )
  const canSelect =
    mode === 'apply' && menuAvailable && availableItems.length > 0

  return (
    <>
      <tr
        className={`border-b border-gray-400 border-solid bg-gray-100 hover:bg-gray-300 ${
          !menuAvailable ? 'opacity-25 bg-gray-100' : ''
        }`}
      >
        <td
          className="px-4 py-2 pl-12"
          colSpan={
            mode === 'apply' ? (showReasons ? 12 : 11) : showReasons ? 11 : 10
          }
        >
          <div className="flex items-center gap-2">
            <button onClick={onToggle} className="focus:outline-none">
              {isExpanded ? (
                <FaChevronUp className="h-4 w-4" />
              ) : (
                <FaChevronDown className="h-4 w-4" />
              )}
            </button>
            <span className="font-medium text-sm">{menu.name}</span>
            {canSelect && (
              <button
                onClick={() => onSelectMenu(menu)}
                className={`ml-1 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold
                  ${
                    checked
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }
                  focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-500`}
                aria-pressed={checked}
                aria-label={checked ? 'Unselect menu' : 'Select menu'}
                title={checked ? 'Selected' : 'Select'}
              >
                {checked ? (
                  <FaCheck className="h-3 w-3" />
                ) : (
                  <FaPlus className="h-3 w-3" />
                )}
                {checked ? 'Selected' : 'Add'}
              </button>
            )}
            <span className="text-gray-600 text-sm ml-4">
              Cutoff:{' '}
              {formatCutoff(
                menu.groupOrderCutoffDays,
                menu.groupOrderCutoffTime,
              )}{' '}
              |{' '}
              <strong>
                {menu.isUniversal ? 'Universal' : 'Non-Universal'}
              </strong>{' '}
              | Avg Margin: {menu.margin.toFixed(2)}%
            </span>
          </div>
        </td>
      </tr>

      {isExpanded &&
        menuAvailable &&
        sortedItems.map((item: PreviewMenuItem, index: number) => {
          const {
            availableModGroups,
            availability,
            hasMods,
            chefPrice,
            tagsDietaryPreferenceList,
            mealType,
            name,
            price,
            margin,
            subsidyCovered,
            employeePays,

            customizedAlternative,
          } = item

          const available = availability.available && menuAvailable
          const dietaryPrefs = (tagsDietaryPreferenceList || '').split(',')
          const { reasons } = availability
          const dimCell = !available ? 'opacity-25 bg-gray-100' : ''
          const modGroupsCount = availableModGroups.length || 0

          return (
            <tr key={index} className="border-b border-gray-300 border-solid">
              {mode === 'apply' && (
                <td
                  className={`pl-1 py-2 text-center ${dimCell}`}
                  style={{ maxWidth: '65px' }}
                >
                  <Checkbox
                    label=""
                    id="exlcude-from-apply"
                    checked={manuallyExcludedItems.has(item.id)}
                    onChange={() => onToggleManuallyExclude(item.id, menu.id)}
                    disabled={!available}
                    width="50px"
                  />
                </td>
              )}
              <td className={`px-4 py-2 pl-5 text-left ${dimCell}`}>
                <div className="text-sm font-medium">{name}</div>
              </td>
              <td className={`px-4 py-2 text-sm text-center ${dimCell}`}>
                {mealType}
              </td>
              <td
                className={`px-4 py-2 text-sm relative ${dimCell}`}
                onMouseEnter={() => {
                  if (item.hasMods && modGroupsCount > 0) {
                    setHoveredModsItemId(item.id)
                  }
                }}
                onMouseLeave={() => setHoveredModsItemId(null)}
              >
                {hasMods && available && modGroupsCount > 0 ? (
                  <div className="inline-block cursor-pointer">
                    <span className="font-bold text-teal-600">
                      Yes ({modGroupsCount})
                    </span>

                    {/* Hover Popover */}
                    {hoveredModsItemId === item.id && (
                      <div
                        className="absolute z-50 left-0 top-full mt-1 bg-white border-solid border-2 border-teal-500 rounded-lg shadow-xl p-4 overflow-y-auto"
                        style={{
                          maxWidth: '400px',
                          minWidth: '300px',
                          maxHeight: '400px',
                        }}
                      >
                        <div className="text-left">
                          <h4 className="font-bold text-sm mb-3 text-teal-700 border-solid border-b pb-2">
                            Modification Groups ({modGroupsCount})
                          </h4>
                          <div className="space-y-3">
                            {item.availableModGroups.map((modGroup) => {
                              const modGroupDim = !modGroup.availability
                                .available
                                ? 'opacity-25 bg-gray-100'
                                : ''

                              return (
                                <div
                                  key={modGroup.id}
                                  className={`border-b border-solid pb-2 last:border-b-0 ${modGroupDim}`}
                                >
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="font-semibold text-gray-800">
                                      {modGroup.name}
                                    </span>
                                    <span className="text-xs font-semibold px-2 py-1 rounded bg-blue-100 text-blue-700">
                                      {capitalizeFirstLetter(modGroup.verb)}
                                    </span>
                                    {modGroup.isRequired && (
                                      <span className="text-xs font-semibold px-2 py-1 rounded bg-red-100 text-red-700">
                                        Required
                                      </span>
                                    )}
                                    {modGroup.isSingleOpt && (
                                      <span className="text-xs font-semibold px-2 py-1 rounded bg-purple-100 text-purple-700">
                                        Single
                                      </span>
                                    )}
                                  </div>
                                  <div className="ml-2 space-y-1">
                                    {modGroup.mods.map((mod) => {
                                      const modItemDim = !mod.availability
                                        .available
                                        ? 'opacity-25 bg-gray-100'
                                        : ''

                                      return (
                                        <div
                                          key={mod.id}
                                          className={`flex justify-between text-xs text-gray-600 ${modItemDim}`}
                                        >
                                          <span>{mod.name}</span>
                                          <span className="font-medium">
                                            {mod.clientPrice > 0
                                              ? `+$${mod.clientPrice.toFixed(
                                                  2,
                                                )}`
                                              : 'Included'}
                                          </span>
                                        </div>
                                      )
                                    })}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : item.hasMods ? (
                  <span>Yes (0)</span>
                ) : (
                  'No'
                )}
              </td>
              <td
                className={`px-4 py-2 text-sm text-center ${dimCell}`}
                style={{ maxWidth: '120px' }}
              >
                <div className="flex gap-1 flex-wrap items-center text-center">
                  <div
                    className={`p-1 rounded-lg ${
                      customizedAlternative && 'bg-green-400 text-white'
                    } flex items-center text-center`}
                  >
                    {customizedAlternative || 'N/A'}
                  </div>
                </div>
              </td>
              <td
                className={`px-4 py-2 text-center ${dimCell}`}
                style={{ maxWidth: '200px' }}
              >
                <div className="flex gap-1 flex-wrap">
                  {dietaryPrefs.map((tag: string) => (
                    <div
                      key={tag}
                      className="text-xs p-1 rounded-lg bg-gray-200 flex items-center"
                    >
                      {tag}
                    </div>
                  ))}
                </div>
              </td>
              <td className={`px-4 py-2 text-sm text-center ${dimCell}`}>
                ${(chefPrice || 0).toFixed(2)}
              </td>
              <td className={`px-4 py-2 text-sm text-center ${dimCell}`}>
                ${(price || 0).toFixed(2)}
              </td>
              <td className={`px-4 py-2 text-sm text-center ${dimCell}`}>
                {(margin || 0).toFixed(2)}%
              </td>
              <td className={`px-4 py-2 text-sm text-center ${dimCell}`}>
                ${(subsidyCovered || 0).toFixed(2)}
              </td>
              <td className={`px-4 py-2 text-sm text-center ${dimCell}`}>
                ${(employeePays || 0).toFixed(2)}
              </td>
              {showReasons && (
                <td
                  className="px-4 py-2 text-sm opacity-100"
                  style={{ maxWidth: '120px' }}
                >
                  {reasons.length > 0 ? (
                    <div className="text-xs font-semibold text-red-700 bg-red-200 p-2 rounded">
                      <ul className="list-inside space-y-1">
                        {item.availability.reasons.map((reason, i) => (
                          <li key={i}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
              )}
            </tr>
          )
        })}
    </>
  )
}
